import { DefaultButton, TextField } from '@fluentui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { ISurveybuttonStyles } from '../../consts';
import { LoaderContext } from '../../routes/router';
import { deleteFile, saveSurveySection } from '../../services/survey';
import { ActionsButton } from './actionButtons';
import { VendorSurveyDropzone } from './surveyDropzone';

export interface IToxicFormType {
    toxicTextSelect: boolean | any;
    toxicFileSelect: boolean | any;
    toxicText?: string;
    toxicFile?: any;
}

interface IProps {
    handlePrevStep: () => void;
    activeStepId: number[];
    handleNewStep: () => void;
    setToxicValues: React.Dispatch<React.SetStateAction<IToxicFormType | null>>;
    toxicValues: IToxicFormType | null;
}

const formSchema = yup.object().shape({
    toxicTextSelect: yup.string().required('This field is required'),
    toxicFileSelect: yup.string().required('This field is required'),
    toxicText: yup.string().when('toxicTextSelect', {
        is: 'true',
        then: yup.string().required('This field is required'),
    }),
    toxicFile: yup.array().when('toxicFileSelect', {
        is: 'true',
        then: yup.array().min(1, 'This field is required').required(),
    }),
});

export const Toxic: React.FC<IProps> = ({
    handleNewStep,
    handlePrevStep,
    activeStepId,
    setToxicValues,
    toxicValues,
}) => {
    const [showTextField, setShowTextField] = React.useState(false);
    const [showFileField, setShowFileField] = React.useState(false);
    const [_, setTest] = React.useState(false);
    const ctxt = React.useContext(LoaderContext);
    const {
        control,
        formState: { errors },
        setValue,
        handleSubmit,
        getValues,
        reset,
    } = useForm({
        resolver: yupResolver(formSchema),
        defaultValues:
            toxicValues !== null
                ? toxicValues
                : {
                      toxicTextSelect: '',
                      toxicFileSelect: '',
                      toxicText: '',
                      toxicFile: [],
                  },
        reValidateMode: 'onChange',
    });

    const onSubmit = React.useCallback(
        (data: IToxicFormType) => {
            const newVal = [
                { questionId: 13, answer: data.toxicTextSelect },
                { questionId: 14, answer: data.toxicText },
                { questionId: 15, answer: data.toxicFileSelect },
                { questionId: 16, answer: data.toxicFile.toString() },
            ];
            setToxicValues(data);
            ctxt?.setLoaderIsActive(true);
            saveSurveySection(newVal).then(() => {
                handleNewStep();
                ctxt?.setLoaderIsActive(false);
            });
        },
        [errors],
    );

    const removeFile = React.useCallback(
        (file: any) => {
            ctxt?.setLoaderIsActive(true);
            deleteFile(file).then(() => {
                const newFiles = [...getValues('toxicFile')];
                setValue(
                    'toxicFile',
                    newFiles.filter((item) => item !== file.toString()),
                );
                ctxt?.setLoaderIsActive(false);
            });
        },
        [getValues('toxicFile')],
    );

    React.useEffect(() => {
        if (toxicValues) {
            reset(toxicValues);
            if (toxicValues?.toxicText?.length) {
                setShowTextField(true);
            }
            if (toxicValues?.toxicFile && toxicValues?.toxicFile[0]?.length) {
                setShowFileField(true);
            }
        }
    }, [toxicValues]);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="survey-main">
                <p className="border-b border-black pb-1 mt-[100px]">Question 1</p>
                <h3>
                    Does the vendor facility use technology, machinery or processes that reduces
                    toxic chemicals?
                </h3>

                <Controller
                    control={control}
                    name="toxicTextSelect"
                    render={({ field }) => (
                        <>
                            <DefaultButton
                                text="Yes"
                                onClick={() => {
                                    setValue('toxicTextSelect', 'true');
                                    setShowTextField(true);
                                }}
                                styles={ISurveybuttonStyles}
                                className={`mr-4  ${
                                    field.value === 'true' ? 'bg-black text-white' : ''
                                }`}
                            />
                            <DefaultButton
                                text="No"
                                onClick={() => {
                                    setValue('toxicTextSelect', 'false');
                                    setShowTextField(false);
                                    setValue('toxicText', '');
                                }}
                                styles={ISurveybuttonStyles}
                                className={`mr-4  ${
                                    field.value === 'false' ? 'bg-black text-white' : ''
                                }`}
                            />
                            <span className="text-red-500">{errors.toxicTextSelect?.message}</span>
                        </>
                    )}
                />
                {showTextField && (
                    <div className="border-l border-black pl-5 mt-[30px]">
                        <span className="subQuestion mb-5">
                            Please outline what technology, machinery or processes the facility uses
                        </span>
                        <>
                            <Controller
                                control={control}
                                name="toxicText"
                                render={({ field }) => (
                                    <TextField
                                        multiline={true}
                                        onChange={field.onChange}
                                        resizable={false}
                                        rows={5}
                                        className="mt-4"
                                        value={field.value}
                                    />
                                )}
                            />
                            <span className="text-red-500">{errors.toxicText?.message}</span>
                        </>
                    </div>
                )}
            </div>
            <div className="survey-main">
                <p className="border-b border-black pb-1 mt-[100px]">Question 2</p>
                <h3>
                    Does the vendor facility have certification(s) with regards to toxic chemicals?
                </h3>

                <Controller
                    control={control}
                    name="toxicFileSelect"
                    render={({ field }) => (
                        <>
                            <DefaultButton
                                text="Yes"
                                onClick={() => {
                                    setValue('toxicFileSelect', 'true');
                                    setShowFileField(true);
                                }}
                                styles={ISurveybuttonStyles}
                                className={`mr-4  ${
                                    field.value === 'true' ? 'bg-black text-white' : ''
                                }`}
                            />
                            <DefaultButton
                                text="No"
                                onClick={() => {
                                    setValue('toxicFileSelect', 'false');
                                    setShowFileField(false);
                                    setValue('toxicFile', ['']);
                                }}
                                styles={ISurveybuttonStyles}
                                className={`mr-4  ${
                                    field.value === 'false' ? 'bg-black text-white' : ''
                                }`}
                            />
                            <span className="text-red-500">{errors.toxicFileSelect?.message}</span>
                        </>
                    )}
                />
                {showFileField && (
                    <div className="border-l border-black pl-5 mt-[30px]">
                        <span className="subQuestion mb-5">
                            Please upload certifications as evidence (relevant certifications are
                            Oeko-Tex 100 – this list is non-exhaustive)
                        </span>
                        <Controller
                            control={control}
                            name="toxicFile"
                            render={() => (
                                <VendorSurveyDropzone
                                    questionId={16}
                                    setValue={setValue}
                                    name="toxicFile"
                                />
                            )}
                        />
                        {getValues('toxicFile') && getValues('toxicFile')[0]?.length !== 0 && (
                            <div className="acceptedFile mt-10">
                                <h3 className="mb-5">Accepted files :</h3>
                                {getValues('toxicFile')?.map((item: any) => (
                                    <div
                                        key={item}
                                        className="flex items-center p-4 py-2 border justify-between mb-2"
                                    >
                                        <p>{item.split('-')[6]}</p>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                removeFile(item);
                                                setTest((prev) => !prev);
                                            }}
                                            className="px-5 py-3 border-l pr-0 text-2xl"
                                        >
                                            X
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <span className="text-red-500 mt-3">{errors.toxicFile?.message}</span>
                    </div>
                )}
            </div>
            <ActionsButton handlePrevStep={handlePrevStep} activeStepId={activeStepId} />
        </form>
    );
};
